import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'transmute/debounce';
import { Map as ImmutableMap } from 'immutable';
import emptyFunction from 'react-utils/emptyFunction';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { FilterIcon } from 'foundations-assets';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { FilterBar } from 'foundations-components/transitional/components/FilterBar';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { List } from 'foundations-components/transitional/components/List';
import { Text } from 'foundations-components/components/Text';
import OwnerSelect from './filterInputs/OwnerSelect';
import MeetingTypeSelect from './filterInputs/MeetingTypeSelect';
import BusinessUnitSelect from './filterInputs/BusinessUnitSelect';
import MeetingNameSearchInput from './filterInputs/MeetingNameSearchInput';
import { RESPONSIVE_TABLE_BREAKPOINTS } from 'MeetingsAdmin/constants/numbers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FILTERS_BY_BREAKPOINT = ImmutableMap({
  [RESPONSIVE_TABLE_BREAKPOINTS.LARGE]: 3,
  [RESPONSIVE_TABLE_BREAKPOINTS.MEDIUM]: 3,
  [RESPONSIVE_TABLE_BREAKPOINTS.SMALL]: 1
});
const MeetingsLinkFilterBar = ({
  filterValues,
  onFilterChange,
  clearFilters,
  isLoading,
  currentUserId,
  hasBusinessUnitsAccess,
  breakpoint
}) => {
  const [searchInputValueLabel, setSearchInputValueLabel] = useState(filterValues.searchInputValue);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const debouncedUpdate = useMemo(() => debounce(500, nextSearchInput => onFilterChange({
    search: nextSearchInput
  })), [onFilterChange]);
  useEffect(() => {
    debouncedUpdate(searchInputValueLabel);
  }, [debouncedUpdate, searchInputValueLabel]);
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setSearchInputValueLabel('');
  }, [clearFilters]);
  const helpMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "filters.helpTooltip"
  });
  const filtersApplied = useMemo(() => {
    const {
      selectedUserId,
      selectedMeetingType,
      selectedBusinessUnitId,
      searchInputValue
    } = filterValues;
    return selectedUserId !== null || selectedMeetingType !== null || selectedBusinessUnitId !== null || searchInputValue !== '';
  }, [filterValues]);
  const renderedOwnerSelect = useMemo(() => {
    const {
      selectedUserId
    } = filterValues;
    return /*#__PURE__*/_jsx(OwnerSelect, {
      disabled: isLoading,
      showAllUsersOption: true,
      fetchMeetingsLinks: emptyFunction,
      setSelectedUserOption: value => value === currentUserId ? onFilterChange({
        userId: null
      }) : onFilterChange({
        userId: value
      }),
      value: selectedUserId || currentUserId
    });
  }, [currentUserId, filterValues, isLoading, onFilterChange]);
  const renderedMeetingTypeSelect = useMemo(() => {
    const {
      selectedMeetingType
    } = filterValues;
    return /*#__PURE__*/_jsx(MeetingTypeSelect, {
      disabled: isLoading,
      value: selectedMeetingType,
      onChange: value => onFilterChange({
        meetingType: value
      })
    });
  }, [filterValues, isLoading, onFilterChange]);
  const renderedBusinessUnitSelect = useMemo(() => {
    if (!hasBusinessUnitsAccess) {
      return null;
    }
    const {
      selectedBusinessUnitId
    } = filterValues;
    return /*#__PURE__*/_jsx(BusinessUnitSelect, {
      disabled: isLoading,
      businessUnitId: selectedBusinessUnitId,
      onChange: value => onFilterChange({
        businessUnitId: value
      })
    });
  }, [filterValues, hasBusinessUnitsAccess, isLoading, onFilterChange]);
  const renderedMeetingNameSearchInput = useMemo(() => {
    return /*#__PURE__*/_jsx(MeetingNameSearchInput, {
      value: searchInputValueLabel,
      onChange: value => setSearchInputValueLabel(value)
    });
  }, [searchInputValueLabel]);
  const filterInputs = [renderedOwnerSelect, renderedMeetingTypeSelect, renderedBusinessUnitSelect].filter(input => input !== null);
  const renderedMoreFilters = useMemo(() => {
    const moreFilters = filterInputs.slice(FILTERS_BY_BREAKPOINT.get(breakpoint));
    if (moreFilters.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(Popover, {
      placement: "bottom",
      open: openMoreFilters,
      onOpenChange: () => setOpenMoreFilters(!openMoreFilters),
      content: {
        header: /*#__PURE__*/_jsx(Text, {
          as: "h4",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "filters.moreFilters"
          })
        }),
        body: /*#__PURE__*/_jsx(List, {
          inline: true,
          children: moreFilters
        })
      },
      paddingBlockEnd: "400",
      children: /*#__PURE__*/_jsxs(Button, {
        padding: "0",
        onClick: () => setOpenMoreFilters(!openMoreFilters),
        use: "transparent",
        children: [/*#__PURE__*/_jsx(FilterIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "filters.moreFilters"
        })]
      })
    });
  }, [breakpoint, filterInputs, openMoreFilters]);
  return /*#__PURE__*/_jsx(FilterBar, {
    startSlot: /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderedMeetingNameSearchInput, filterInputs.slice(0, FILTERS_BY_BREAKPOINT.get(breakpoint)), renderedMoreFilters, /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "clear-filters-button",
        use: "transparent",
        onClick: handleClearFilters,
        disabled: isLoading || !filtersApplied,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "filters.clearFilters"
        })
      })]
    }),
    endSlot: /*#__PURE__*/_jsx(HelpIcon, {
      title: helpMessage,
      tooltipPlacement: "left",
      size: "700"
    })
  });
};
export default MeetingsLinkFilterBar;